import React, {useEffect, useMemo, useState} from 'react'
import {observer} from 'mobx-react'
import {Button, Col, Layout, Modal, Row, Typography} from 'antd'
import {InfoCircleOutlined} from '@ant-design/icons'
import {Link} from 'react-router-dom'
import {useInjection} from 'dna-react-ioc'
import {useTranslation} from 'react-i18next'
import {
    PageHeader,
    RangePickerContainer,
    SelectWithTitleContainer,
    TransactionsCardWithBarChartContainer,
    TransactionSummaryListContainer
} from '~/code/components'
import {TerminalType} from '~/code/constants'
import {withTitle} from '~/code/router/utils'
import {generateChartTitleForRange} from '~/code/stores'
import {TSettlementsTransactionsCardWithBarChartContainerStore} from '~/code/stores/reports/SettlementsTransactionsCardWithBarChartContianerStore/TSettlementsTransactionsCardWithBarChartContainerStore'
import {PerSettlementDateTransactionsHistoryTable} from './components'

import styles from './styles.scss'
import {Moment} from 'moment'
import {isOctoberRestrictionEnabled} from '~/code/constants/dateRangePresets'
import {disabledDatesBeforeOctober2025} from '~/code/utils/moment-utils'
import {isDateDisabled} from '../../Overview/components/utils'

export const PerSettlementDateTransactionSummaryStoreSymbol = Symbol(
    'PerSettlementDateTransactionSummaryStore'
)
export const PerSettlementDateTransactionsCardWithBarChartContainerStoreSymbol = Symbol(
    'PerSettlementDateTransactionsCardWithBarChartContainerStore'
)
export const PerSettlementDateRangePickerStoreSymbol = Symbol('PerSettlementDateRangePickerStore')
export const PerSettlementDateCurrencySelectStoreSymbol = Symbol(
    'PerSettlementDateCurrencySelectStore'
)

type NullableRange = [Moment | null, Moment | null] | null

const PerSettlementDate = observer(() => {
    const {t} = useTranslation()
    const transactionStore = useInjection<TSettlementsTransactionsCardWithBarChartContainerStore>(
        PerSettlementDateTransactionsCardWithBarChartContainerStoreSymbol
    )

    const {interval} = transactionStore

    const [isModalOpen, setModal] = useState(false)
    const [rangeTypeSelected, setRangeTypeSelected] = useState<'start' | 'end' | null>(null)
    const [localSelectedDates, setLocalSelectedDates] = useState<NullableRange>(() => [
        interval.from,
        interval.to
    ])
    const [tempSelectedDates, setTempSelectedDates] = useState<NullableRange>(null)
    const [isCalendarChanged, setCalendarChanged] = useState(false)
    const [isDateClicked, setDateClicked] = useState(false)

    useEffect(() => {
        setLocalSelectedDates([interval.from, interval.to])
    }, [interval])

    const handleCalendarChange = (
        dates: [Moment, Moment],
        range: 'start' | 'end'
    ): [Moment, Moment] => {
        setRangeTypeSelected(range)
        setTempSelectedDates([dates?.[0] ?? null, dates?.[1] ?? null])
        setCalendarChanged(true)

        return dates
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setRangeTypeSelected(null)
            setLocalSelectedDates(undefined)
            setCalendarChanged(false)
            setDateClicked(false)
        }
    }

    const chartTitle = useMemo(() => {
        const rangeTitle = generateChartTitleForRange(transactionStore.interval) || ''
        return `${t('OverviewSettlementsAnalytics.settlementsHistory')} ${rangeTitle}`
    }, [transactionStore.interval])

    const pageHeaderExtra = () => (
        <div className={styles.pageHeaderExtra}>
            <SelectWithTitleContainer
                injectableIdentifier={PerSettlementDateCurrencySelectStoreSymbol}
                displayValue={t('Overview.currency')}
            />
            <RangePickerContainer
                value={localSelectedDates}
                cellRender={(current, info) => {
                    const originNode = info.originNode

                    return React.cloneElement(originNode, {
                        onClick: (e: React.MouseEvent) => {
                            originNode.props?.onClick?.(e)
                            setDateClicked(true)
                        }
                    })
                }}
                disabledDate={(currentDate, selectedDates) => {
                    if (
                        isOctoberRestrictionEnabled &&
                        disabledDatesBeforeOctober2025(currentDate)
                    ) {
                        return true
                    }

                    return isDateDisabled(
                        currentDate,
                        tempSelectedDates || selectedDates,
                        rangeTypeSelected,
                        isCalendarChanged,
                        isDateClicked
                    )
                }}
                onOpenChange={(open) => {
                    handleOpenChange(open)
                }}
                onCalendarChange={(dates, _, info) => {
                    handleCalendarChange(dates, info.range)
                }}
                showRangePickerTime={false}
                injectableIdentifier={PerSettlementDateRangePickerStoreSymbol}
            />
        </div>
    )

    return (
        <>
            <PageHeader
                className={styles.header}
                title={
                    <Row gutter={[8, 8]}>
                        <Col>
                            <Typography.Title className={styles.title} level={4}>
                                {t('Settlements.title')}
                            </Typography.Title>
                        </Col>
                        <Col>
                            <Link to={'#'} onClick={() => setModal(true)}>
                                <InfoCircleOutlined className={styles.popupIcon} />
                            </Link>
                        </Col>
                    </Row>
                }
                extra={pageHeaderExtra()}
                border={'none'}
            />

            <Layout.Content className={styles.body}>
                <Row gutter={[16, 16]}>
                    <Col span={24} xs={24}>
                        <TransactionSummaryListContainer
                            type={TerminalType.SETTLEMENTS}
                            injectableIdentifier={PerSettlementDateTransactionSummaryStoreSymbol}
                        />
                    </Col>
                    <Col span={24}>
                        <TransactionsCardWithBarChartContainer
                            title={chartTitle}
                            injectableIdentifier={
                                PerSettlementDateTransactionsCardWithBarChartContainerStoreSymbol
                            }
                        />
                    </Col>
                    <Col span={24} xs={24}>
                        <PerSettlementDateTransactionsHistoryTable />
                    </Col>
                </Row>
            </Layout.Content>
            <Modal
                title={t('Settlements.perSettlementDateModalTitle')}
                open={isModalOpen}
                onCancel={() => setModal(false)}
                footer={
                    <div>
                        <Button type='default' onClick={() => setModal(false)}>
                            {t('Settlements.close')}
                        </Button>
                    </div>
                }
            >
                <ul>
                    <li>{t('Settlements.perSettlementDateModalText1')}</li>
                    <li>{t('Settlements.perSettlementDateModalText2')}</li>
                </ul>
            </Modal>
        </>
    )
})

export default withTitle(PerSettlementDate)
